import type { LucideIcon } from "lucide-react";
import { Globe, Search, MapPin, Megaphone, Share2, Sparkles } from "lucide-react";

export type PricingTier = {
  name: string;
  price: string;
  period: string;
  tag: string;
  desc: string;
  features: string[];
  includedPages?: number;
  notIncluded: string[];
  timeline: string;
  faqs: { q: string; a: string }[];
  featured?: boolean;
  accent: "gold" | "sky" | "emerald" | "violet" | "amber";
};

export type ServicePricing = {
  slug: string;
  routePath: string;
  service: string;
  shortName: string;
  icon: LucideIcon;
  startingAt: string;
  oneLiner: string;
  description: string;
  heroEyebrow: string;
  heroTitle: string;
  heroGoldWord: string;
  heroDescription: string;
  metaTitle: string;
  metaDescription: string;
  tiers: PricingTier[];
};

const sharedTrustFaq = {
  q: "Are there long-term contracts?",
  a: "No. All ongoing services are month-to-month. We earn your business every single month — if we're not delivering, you can cancel anytime.",
};

// ---------------------------------------------------------------------------
// CANONICAL TIERS — cards and calculator options derive from these definitions.
// ---------------------------------------------------------------------------

export const webDesignTiers: PricingTier[] = [
  {
    name: "Starter Website",
    includedPages: 4,
    price: "$1,850",
    period: "one-time",
    tag: "Getting Started",
    desc: "A professional 4-page website — Home, About, Services, Contact — up and running fast.",
    accent: "sky",
    features: [
      "4 custom-designed pages (Home, About, Services, Contact)",
      "SEO-ready build — clean code, fast, indexable, ready for any SEO you choose",
      "Lead form + click-to-call buttons",
      "1 year Cloudflare hosting + SSL",
    ],
    notIncluded: [
      "Reviews page",
      "Ongoing monthly marketing",
      "Paid ad management",
    ],
    timeline: "Typical sites: less than 7 business days",
    faqs: [
      {
        q: "What if I need more than 4 pages?",
        a: "Each additional page is $85 — or step up to the Pro Website with 10 pages included.",
      },
      {
        q: "Do I own the website?",
        a: "Yes, 100%. You own the domain, the design, and the code.",
      },
      sharedTrustFaq,
    ],
  },
  {
    name: "Pro Website",
    includedPages: 10,
    price: "$2,750",
    period: "one-time",
    tag: "Most Popular",
    desc: "An updated, more professional website for established businesses with up to 10 pages.",
    accent: "gold",
    featured: true,
    features: [
      "Up to 10 custom-designed pages",
      "Foundation SEO done for you (titles, meta, schema, sitemap)",
      "Analytics setup",
      "Lead form + click-to-call buttons",
      "Built-in traffic dashboard — see your traffic and pages anytime",
      "1 year Cloudflare hosting + SSL",
    ],
    notIncluded: [
      "Ongoing monthly marketing",
      "Blog or content writing",
      "Paid ad management",
    ],
    timeline: "Typical sites: less than 7 business days",
    faqs: [
      {
        q: "What if I need more than 10 pages?",
        a: "Each additional page is $85.",
      },
      {
        q: "Do I own the website?",
        a: "Yes, 100%. You own the domain, the design, and the code.",
      },
      sharedTrustFaq,
    ],
  },
  {
    name: "Expert Website",
    includedPages: 50,
    price: "$5,950",
    period: "one-time",
    tag: "Multi-City Growth",
    desc: "Up to 50 pages covering multiple cities and services — one site built to match your footprint.",
    accent: "violet",
    features: [
      "Up to 50 custom-designed pages",
      "Service × city page architecture built to rank",
      "Advanced SEO & AI setup",
      "Lead form + click-to-call buttons",
      "Built-in traffic dashboard — see your traffic and pages anytime",
      "1 year Cloudflare hosting + SSL",
    ],
    notIncluded: [
      "Ongoing monthly marketing",
      "Paid ad management",
    ],
    timeline: "Typical sites: less than 7 business days; larger scopes confirmed before kickoff",
    faqs: [
      {
        q: "Is 50 pages really included?",
        a: "Yes — up to 50 pages are included in the $5,950. That's a big savings versus adding 40 pages individually to the Pro package.",
      },
      {
        q: "Do I own the website?",
        a: "Yes, 100%. You own the domain, the design, and the code.",
      },
      sharedTrustFaq,
    ],
  },
];

export const leadGenTiers: PricingTier[] = [
  {
    name: "Starter Marketing",
    price: "$950",
    period: "/ month",
    tag: "Getting Started",
    desc: "Local businesses building their visibility on Google Search and Google Maps.",
    accent: "sky",
    features: [
      "Local SEO",
      "Google Maps rankings",
    ],
    notIncluded: ["AI & GEO optimization", "Social media marketing", "PPC ad management", "Reputation management"],
    timeline: "Significant lift in 90–120 days",
    faqs: [
      {
        q: "Do you guarantee rankings?",
        a: "No ethical agency does — Google forbids it. What we do is proven, transparent work you can verify.",
      },
      sharedTrustFaq,
    ],
  },
  {
    name: "Growth Marketing",
    price: "$1,650",
    period: "/ month",
    tag: "Most Popular",
    desc: "Google, Maps, AI search and social handled for you.",
    accent: "gold",
    featured: true,
    features: [
      "Local SEO",
      "Google Maps rankings",
      "AI & GEO optimization",
      "Social Media Marketing",
    ],
    notIncluded: ["PPC ad management", "Reputation management", "Ad spend"],
    timeline: "Significant lift in 90–120 days",
    faqs: [
      {
        q: "Do you guarantee rankings?",
        a: "No ethical agency does — Google forbids it. What we do is proven, transparent work you can verify.",
      },
      sharedTrustFaq,
    ],
  },
  {
    name: "Expert Marketing",
    price: "$2,750",
    period: "/ month",
    tag: "Multi-City Growth",
    desc: "Multiple cities and services, plus paid ads.",
    accent: "violet",
    features: [
      "Multi-city local SEO",
      "Google Maps rankings",
      "AI & GEO optimization",
      "Social Media Marketing",
      "PPC ad management",
      "Reputation management",
    ],
    notIncluded: ["Ad spend"],
    timeline: "Significant lift in 90–120 days",
    faqs: [
      {
        q: "Is ad spend included?",
        a: "No. Ad spend is not included — it's billed directly by the ad platform and is separate from the monthly management fee.",
      },
      sharedTrustFaq,
    ],
  },
];



// ---------------------------------------------------------------------------
// SERVICE_PRICING — one entry per dedicated service route.
// Web design uses webDesignTiers; everything else uses leadGenTiers because
// SEO, Google Maps, PPC, social and AI/GEO are all delivered as part of the
// Lead Generation programs on the live /pricing page.
// ---------------------------------------------------------------------------

export const SERVICE_PRICING: ServicePricing[] = [
  {
    slug: "web-design",
    routePath: "/pricing",
    service: "Web Design",
    shortName: "Web Design",
    icon: Globe,
    startingAt: "$1,850",
    oneLiner: "Custom, mobile-first websites built to convert.",
    description:
      "Fast, conversion-focused websites for Colorado service businesses. No templates, no DIY page builders.",
    heroEyebrow: "Web Design Pricing",
    heroTitle: "Transparent web design pricing for Colorado businesses",
    heroGoldWord: "Transparent",
    heroDescription:
      "Real prices. No 'request a quote' games. Pick the package that matches where your business is today.",
    metaTitle: "Web Design Pricing Colorado Springs | Custom Websites from $1,850",
    metaDescription:
      "Honest, public web design pricing for Colorado businesses. Custom, mobile-first websites starting at $1,850 one-time. See exactly what's included.",
    tiers: webDesignTiers,
  },
  {
    slug: "seo",
    routePath: "/pricing",
    service: "SEO Services",
    shortName: "SEO",
    icon: Search,
    startingAt: "$950/mo",
    oneLiner: "Rank on Page 1 for the searches your customers actually make.",
    description:
      "Local & organic SEO for Colorado businesses, delivered as part of our Lead Generation programs.",
    heroEyebrow: "SEO Pricing",
    heroTitle: "SEO pricing built for Colorado service businesses",
    heroGoldWord: "Colorado",
    heroDescription:
      "SEO is the foundation of every Lead Generation program. Pick the level of investment that matches your market.",
    metaTitle: "SEO Pricing Colorado Springs | Local SEO from $950/month",
    metaDescription:
      "Transparent SEO pricing for Colorado businesses. Local SEO is included in every Lead Generation program, starting at $950/month. Month-to-month, no contracts.",
    tiers: leadGenTiers,
  },
  {
    slug: "google-maps",
    routePath: "/pricing",
    service: "Google Maps Optimization",
    shortName: "Google Maps",
    icon: MapPin,
    startingAt: "$950/mo",
    oneLiner: "Rank in the Google Map Pack where local buyers actually click.",
    description:
      "Google Business Profile and Map Pack optimization, included in our Lead Generation programs.",
    heroEyebrow: "Google Maps Pricing",
    heroTitle: "Google Maps pricing that gets you in the 3-pack",
    heroGoldWord: "3-pack",
    heroDescription:
      "Map Pack ranking and Google Business Profile management are part of every Lead Generation package. No standalone GBP-only product.",
    metaTitle: "Google Maps Pricing Colorado | GBP Optimization from $950/mo",
    metaDescription:
      "Google Maps optimization for Colorado service businesses. Included in every Lead Generation program, starting at $950/month. Month-to-month, no contracts.",
    tiers: leadGenTiers,
  },
  {
    slug: "ppc",
    routePath: "/pricing",
    service: "Google Ads Management",
    shortName: "PPC / Google Ads",
    icon: Megaphone,
    startingAt: "$2,750/mo",
    oneLiner: "Profitable Google Ads engineered for qualified phone calls.",
    description:
      "Performance-driven Google Ads management, delivered inside our Expert Marketing program. Pay for results, not impressions.",
    heroEyebrow: "Google Ads Pricing",
    heroTitle: "Google Ads pricing built for ROI, not impressions",
    heroGoldWord: "ROI",
    heroDescription:
      "Paid ad management is included in our Expert Marketing program ($2,750/mo). Ad spend not included — it's billed directly by the ad platform so you see every dollar.",
    metaTitle: "Google Ads Pricing Colorado | PPC Management from $2,750/mo",
    metaDescription:
      "Transparent ad management across platforms for Colorado service businesses. Included in our $2,750/mo Expert Marketing program. Ad spend not included.",
    tiers: leadGenTiers,
  },
  {
    slug: "social-media",
    routePath: "/pricing",
    service: "Social Media Marketing",
    shortName: "Social Media",
    icon: Share2,
    startingAt: "$1,650/mo",
    oneLiner: "Branded social content that makes your business look active and trustworthy.",
    description:
      "Facebook and Instagram content + engagement, included in our Growth and Expert Marketing programs.",
    heroEyebrow: "Social Media Pricing",
    heroTitle: "Social media pricing for Colorado businesses",
    heroGoldWord: "Colorado",
    heroDescription:
      "Social media is part of our Lead Generation programs — no standalone social-only retainer. Pick the level that matches your investment.",
    metaTitle: "Social Media Pricing Colorado | Content + Engagement from $1,650/mo",
    metaDescription:
      "Social media management for Colorado businesses. Included in our $1,650/mo Growth Marketing program. Month-to-month, no contracts.",
    tiers: leadGenTiers,
  },
  {
    slug: "geo-ai",
    routePath: "/pricing",
    service: "GEO & AI Search Optimization",
    shortName: "GEO & AI",
    icon: Sparkles,
    startingAt: "$1,650/mo",
    oneLiner: "Get cited by ChatGPT, Perplexity, and Google's AI overviews.",
    description:
      "Generative engine optimization (GEO), included in Growth and Expert Marketing.",
    heroEyebrow: "GEO & AI Pricing",
    heroTitle: "AI search optimization pricing for the new search era",
    heroGoldWord: "AI",
    heroDescription:
      "AI / GEO optimization is part of Growth and Expert Marketing — get cited by ChatGPT, Gemini, Perplexity and Google AI Overviews.",
    metaTitle: "AI / GEO Optimization Pricing Colorado | From $1,650/mo",
    metaDescription:
      "Generative Engine Optimization for Colorado businesses. Included in Growth and Expert Marketing, from $1,650/mo. Get cited by ChatGPT, Perplexity and Google AI Overviews.",
    tiers: leadGenTiers,
  },
];

export const SERVICE_PRICING_BY_SLUG: Record<string, ServicePricing> = Object.fromEntries(
  SERVICE_PRICING.map((s) => [s.slug, s]),
);
